import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import { Button, CdnIcon, DatePicker, Heading, PlainButton, Select, TRANSFORMER_MAX_MOBILE_WIDTH_PX, TravelerSelection, cn } from "@pcln/horizon";
import { addDays } from "date-fns/addDays";
import { addMonths } from "date-fns/addMonths";
import { Fragment as external_react_Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useMediaQuery } from "react-responsive";
import { BundleAndSave } from "../shared/components/BundleAndSave.js";
import { SearchFormRadioGroup } from "../shared/components/SearchFormRadioGroup.js";
import { SubmitButton } from "../shared/components/SubmitButton.js";
import { DEFAULT_COLLISION_AVOIDANCE } from "../shared/constants.js";
import { useContainerBreakpoint } from "../shared/hooks/useContainerBreakpoint.js";
import { useTabGroup } from "../shared/hooks/useTabGroup.js";
import { createYupResolver } from "../shared/validation/yup-form-resolver.js";
import { CABIN_OPTIONS, FLIGHT_DEFAULT_VALUES, FLIGHT_LABELS, FLIGHT_PLACEHOLDERS, MULTI_DESTINATION, ONE_WAY, ROUND_TRIP } from "./constants.js";
import { getFlightTypeClass, getGridContainerClass, getInputClass, getRouteSwapButtonClass, getWideLayoutBreakpointPx } from "./FlightSearchForm.variants.js";
import { addFlightOnClick, createRadioOptions, getAllowedFlightTypes, getMultiDestDefaultMonth, getMultiDestDisabledDates, getMultiDestinationSelectedDate, getSelectedDates, normalizeInitialFlightValues, onCalendarChange } from "./formHandlers.js";
import validation from "./validation.js";
function getDateFieldErrorMessage(errors, i, isRoundTripFlight = false) {
    return errors.flights?.[i]?.startDate?.message || errors.flights?.[i]?.endDate?.message ? isRoundTripFlight ? 'Please select a departing and returning date' : 'Please select a departing date' : void 0;
}
const DEFAULT_INITIAL_VALUES = {
    flightType: ROUND_TRIP,
    flights: [
        {
            startLocation: {},
            endLocation: {},
            startDate: '',
            endDate: ''
        }
    ],
    tripType: 'FLY',
    cabinClass: 'Economy',
    travelers: {
        adults: 1,
        children: 0,
        childrenAges: {},
        rooms: 0,
        lapInfants: 0
    }
};
function FlightSearchForm({ onSubmit, initialValues, isMobile = false, renderAutocomplete, showBundleAndSave = true, showCabinClass = true, flightTypes, removeBackground = false, onBundleChange, onItemClick, autoAdvance = false, onInfantError, isTravelerSelectionDisabled = false, submitLabel, submitButtonType = 'primary', submitButtonEmphasis, showRouteSwapButton = false, renderDatePicker, collisionAvoidance = DEFAULT_COLLISION_AVOIDANCE, singleRow = false }) {
    const allowedFlightTypes = useMemo(()=>getAllowedFlightTypes(flightTypes), [
        flightTypes
    ]);
    const mergedInitialValues = useMemo(()=>normalizeInitialFlightValues({
            ...DEFAULT_INITIAL_VALUES,
            ...initialValues
        }, allowedFlightTypes), [
        initialValues,
        allowedFlightTypes
    ]);
    const { registerTabElement, focusNextElement } = useTabGroup();
    const fieldRefs = useRef(new Map());
    const isMobileComponent = useMediaQuery({
        maxWidth: TRANSFORMER_MAX_MOBILE_WIDTH_PX
    });
    const containerRef = useRef(null);
    const isWideLayout = useContainerBreakpoint(containerRef, getWideLayoutBreakpointPx(singleRow));
    const makeInputRef = useCallback((key)=>{
        if (!autoAdvance) return;
        return (el)=>{
            if (el) fieldRefs.current.set(key, el);
            registerTabElement(el);
        };
    }, [
        autoAdvance,
        registerTabElement
    ]);
    const { control, setValue, handleSubmit, clearErrors, formState: { errors } } = useForm({
        defaultValues: mergedInitialValues,
        mode: 'onSubmit',
        resolver: createYupResolver(validation),
        reValidateMode: 'onChange'
    });
    const watchFlightType = useWatch({
        control,
        name: 'flightType',
        defaultValue: mergedInitialValues.flightType
    });
    const watchFlights = useWatch({
        control,
        name: 'flights',
        defaultValue: mergedInitialValues.flights
    });
    const watchCabinClass = useWatch({
        control,
        name: 'cabinClass',
        defaultValue: mergedInitialValues.cabinClass
    });
    const watchTravelers = useWatch({
        control,
        name: 'travelers',
        defaultValue: mergedInitialValues.travelers
    });
    const watchTripType = useWatch({
        control,
        name: 'tripType',
        defaultValue: mergedInitialValues.tripType
    });
    useEffect(()=>{
        clearErrors('flights');
    }, [
        clearErrors,
        watchFlights
    ]);
    useEffect(()=>{
        if (watchTravelers) clearErrors('travelers');
    }, [
        clearErrors,
        watchTravelers
    ]);
    const [addHotel, setAddHotel] = useState(false);
    const [addCar, setAddCar] = useState(false);
    const flights = useMemo(()=>watchFlights ?? [
            {
                startLocation: {},
                endLocation: {},
                startDate: '',
                endDate: ''
            }
        ], [
        watchFlights
    ]);
    const flightKeyCounter = useRef(flights.length);
    const [flightKeys, setFlightKeys] = useState(()=>Array.from({
            length: flights.length
        }, (_, i)=>i));
    const isPackage = addHotel || addCar;
    const hideCabin = isPackage || !showCabinClass;
    const onItemSelect = useCallback((searchKey, item)=>{
        setValue(searchKey, item);
        if (isMobile || !item) {
            if (isMobile && !!item && document.activeElement instanceof HTMLElement) document.activeElement.blur();
        } else {
            onItemClick?.();
            if (autoAdvance) {
                const fromEl = fieldRefs.current.get(searchKey);
                focusNextElement(true, fromEl);
            }
        }
    }, [
        setValue,
        isMobile,
        onItemClick,
        autoAdvance,
        focusNextElement
    ]);
    const [bundleSelections, setBundleSelections] = useState({
        STAY: addHotel,
        DRIVE: addCar
    });
    const handleBundleSelectionChange = useCallback((updatedTripType, checked, optionValue)=>{
        setBundleSelections((prev)=>({
                ...prev,
                [optionValue]: checked
            }));
        setValue('tripType', updatedTripType);
        if (checked && onBundleChange) onBundleChange(updatedTripType, checked, optionValue);
        if ('STAY' === optionValue) {
            setAddHotel(checked);
            setValue('travelers', {
                ...watchTravelers,
                rooms: checked ? 1 : 0
            });
        } else if ('DRIVE' === optionValue) setAddCar(checked);
    }, [
        setValue,
        watchTravelers,
        onBundleChange
    ]);
    useEffect(()=>{
        if (watchFlightType !== ROUND_TRIP && (addHotel || addCar)) {
            setAddHotel(false);
            setAddCar(false);
            setBundleSelections({
                STAY: false,
                DRIVE: false
            });
            setValue('tripType', 'FLY');
            setValue('travelers', {
                ...watchTravelers,
                rooms: 0
            });
        }
    }, [
        watchFlightType
    ]);
    const currentDate = new Date();
    const [maxSelectableDate, setMaxSelectableDate] = useState();
    const [defaultMonth, setDefaultMonth] = useState(currentDate);
    const handleFlightCalendarChange = useCallback((dates)=>{
        onCalendarChange(dates, setDefaultMonth, setValue, 0);
        addHotel && dates && 'object' == typeof dates && 'from' in dates && dates.from && dates?.to === void 0 ? setMaxSelectableDate(addDays(dates.from, 28)) : setMaxSelectableDate(void 0);
    }, [
        addHotel,
        setValue
    ]);
    const calendarStartMonth = useMemo(()=>new Date(), []);
    const calendarEndMonth = useMemo(()=>addMonths(new Date(), 11), []);
    const isRoundTripFlight = (watchFlightType || FLIGHT_DEFAULT_VALUES.FLIGHT_TYPE) === ROUND_TRIP;
    const datePickerMode = isRoundTripFlight ? 'range' : 'single';
    const datePickerTitle = isRoundTripFlight ? 'Flights: Departing – Returning' : 'Flights: Departing';
    const datePickerLabel = isRoundTripFlight ? 'Departing – Returning' : 'Departing';
    const datePickerNumberOfMonths = isMobileComponent ? 12 : 2;
    const datePickerMin = addHotel ? 1 : 0;
    const datePickerSelected = useMemo(()=>getSelectedDates(0, flights, watchFlightType, FLIGHT_DEFAULT_VALUES.FLIGHT_TYPE), [
        flights,
        watchFlightType
    ]);
    const datePickerErrorMessage = useMemo(()=>getDateFieldErrorMessage(errors, 0, isRoundTripFlight), [
        errors,
        isRoundTripFlight
    ]);
    const lapInfantError = errors?.travelers?.lapInfants?.message;
    useEffect(()=>{
        if (lapInfantError && onInfantError) onInfantError();
    }, [
        lapInfantError,
        onInfantError
    ]);
    const handleSubmitHelper = handleSubmit((data)=>{
        onSubmit(data);
    });
    const handleSwapRoute = useCallback(()=>{
        const firstFlight = flights[0];
        if (!firstFlight) return;
        setValue('flights.0.startLocation', firstFlight.endLocation, {
            shouldDirty: true
        });
        setValue('flights.0.endLocation', firstFlight.startLocation, {
            shouldDirty: true
        });
    }, [
        flights,
        setValue
    ]);
    const canShowRouteSwapButton = showRouteSwapButton && watchFlightType !== MULTI_DESTINATION;
    const renderBundleAndSaveSection = ()=>{
        if (!showBundleAndSave || watchFlightType !== ROUND_TRIP) return null;
        return /*#__PURE__*/ jsx("div", {
            className: getInputClass('packageDeals', watchFlightType, singleRow),
            children: /*#__PURE__*/ jsx(BundleAndSave, {
                productType: "flights",
                checkedValues: bundleSelections,
                onSelectionChange: handleBundleSelectionChange,
                tripType: watchTripType,
                removeBackground: removeBackground
            })
        });
    };
    return /*#__PURE__*/ jsx("div", {
        ref: containerRef,
        className: "@container w-auto",
        children: /*#__PURE__*/ jsx("form", {
            onSubmit: handleSubmitHelper,
            className: "w-auto",
            "aria-label": "horizon-flight-search-form",
            children: /*#__PURE__*/ jsx("div", {
                className: "mx-auto w-auto",
                children: /*#__PURE__*/ jsxs("div", {
                    className: getGridContainerClass(watchFlightType, addHotel, hideCabin, singleRow),
                    children: [
                        /*#__PURE__*/ jsxs("div", {
                            className: getFlightTypeClass(watchFlightType || FLIGHT_DEFAULT_VALUES.FLIGHT_TYPE, singleRow),
                            children: [
                                /*#__PURE__*/ jsx(SearchFormRadioGroup, {
                                    name: "flightType",
                                    options: createRadioOptions(allowedFlightTypes),
                                    defaultValue: mergedInitialValues.flightType,
                                    required: true,
                                    "aria-labelledby": "flight-type",
                                    handleChange: (value)=>{
                                        setValue('flightType', value);
                                    }
                                }),
                                !hideCabin && /*#__PURE__*/ jsx("div", {
                                    className: cn('hidden', singleRow ? '@5xl:block' : '@7xl:block'),
                                    children: /*#__PURE__*/ jsx(Select, {
                                        size: "sm",
                                        label: FLIGHT_LABELS.CABIN_CLASS,
                                        menuItems: [
                                            ...CABIN_OPTIONS
                                        ],
                                        initialValue: watchCabinClass || FLIGHT_DEFAULT_VALUES.CABIN_CLASS,
                                        onChange: (value)=>{
                                            setValue('cabinClass', value, {
                                                shouldDirty: true
                                            });
                                        },
                                        placeholder: FLIGHT_PLACEHOLDERS.CABIN_CLASS,
                                        isLabelHidden: true
                                    })
                                })
                            ]
                        }),
                        watchFlightType === MULTI_DESTINATION ? /*#__PURE__*/ jsxs(Fragment, {
                            children: [
                                flights.map((flight, index)=>/*#__PURE__*/ jsxs(external_react_Fragment, {
                                        children: [
                                            /*#__PURE__*/ jsxs("div", {
                                                className: "col-span-full flex items-baseline gap-3",
                                                children: [
                                                    /*#__PURE__*/ jsxs(Heading, {
                                                        as: "h6",
                                                        children: [
                                                            "Flight ",
                                                            index + 1
                                                        ]
                                                    }),
                                                    index > 0 && /*#__PURE__*/ jsx(PlainButton, {
                                                        type: "primary",
                                                        textStyle: "body2",
                                                        buttonType: "button",
                                                        slots: {
                                                            textSlot: 'no-underline hover:underline'
                                                        },
                                                        onClick: ()=>{
                                                            const updatedFlights = [
                                                                ...flights.slice(0, index),
                                                                ...flights.slice(index + 1)
                                                            ];
                                                            setValue('flights', updatedFlights);
                                                            setFlightKeys((prev)=>[
                                                                    ...prev.slice(0, index),
                                                                    ...prev.slice(index + 1)
                                                                ]);
                                                        },
                                                        "aria-label": `Remove flight ${index + 1}`,
                                                        children: "Remove"
                                                    })
                                                ]
                                            }),
                                            /*#__PURE__*/ jsx("div", {
                                                children: renderAutocomplete?.({
                                                    searchKey: `flights.${index}.startLocation`,
                                                    label: FLIGHT_PLACEHOLDERS.ORIGIN,
                                                    typeAheadEndpoint: 'flights',
                                                    errorMessage: errors.flights?.[index]?.startLocation?.message,
                                                    initialSelectedItem: flight.startLocation,
                                                    onItemSelect: (item)=>onItemSelect(`flights.${index}.startLocation`, item ?? void 0),
                                                    inputRef: makeInputRef(`flights.${index}.startLocation`)
                                                })
                                            }),
                                            /*#__PURE__*/ jsx("div", {
                                                children: renderAutocomplete?.({
                                                    searchKey: `flights.${index}.endLocation`,
                                                    label: FLIGHT_PLACEHOLDERS.DESTINATION,
                                                    typeAheadEndpoint: 'flights',
                                                    errorMessage: errors.flights?.[index]?.endLocation?.message,
                                                    initialSelectedItem: flight.endLocation,
                                                    onItemSelect: (item)=>onItemSelect(`flights.${index}.endLocation`, item ?? void 0),
                                                    inputRef: makeInputRef(`flights.${index}.endLocation`)
                                                })
                                            }),
                                            /*#__PURE__*/ jsx("div", {
                                                className: getInputClass('multiDestDateWrapper'),
                                                children: /*#__PURE__*/ jsx(DatePicker, {
                                                    className: "flight-dates",
                                                    title: "Flights: Departure",
                                                    label: "Departing",
                                                    mode: "single",
                                                    numberOfMonths: isMobileComponent ? 12 : 2,
                                                    selected: getMultiDestinationSelectedDate(index, flights),
                                                    errorMessage: getDateFieldErrorMessage(errors, index, isRoundTripFlight),
                                                    handleCalendarChange: (dates)=>{
                                                        onCalendarChange(dates, setDefaultMonth, setValue, index);
                                                    },
                                                    disabled: {
                                                        before: getMultiDestDisabledDates(flights[index - 1]?.startDate)
                                                    },
                                                    startMonth: currentDate,
                                                    endMonth: addMonths(currentDate, 11),
                                                    defaultMonth: getMultiDestDefaultMonth(flights[index - 1]?.startDate, flight.startDate),
                                                    collisionAvoidance: collisionAvoidance
                                                })
                                            })
                                        ]
                                    }, `flight-${flightKeys[index]}`)),
                                /*#__PURE__*/ jsx("div", {
                                    className: "col-span-full @7xl:[grid-area:unset]",
                                    children: /*#__PURE__*/ jsx(Button, {
                                        type: "secondary",
                                        size: "sm",
                                        buttonType: "button",
                                        onClick: ()=>{
                                            addFlightOnClick(setValue, watchFlights);
                                            setFlightKeys((prev)=>[
                                                    ...prev,
                                                    flightKeyCounter.current++
                                                ]);
                                        },
                                        disabled: watchFlights && watchFlights.length >= 5,
                                        children: "Add Another Flight"
                                    })
                                })
                            ]
                        }) : /*#__PURE__*/ jsxs(Fragment, {
                            children: [
                                /*#__PURE__*/ jsxs("div", {
                                    className: cn(getInputClass('origin', void 0, singleRow), canShowRouteSwapButton && (singleRow ? '@5xl:relative' : '@3xl:relative')),
                                    children: [
                                        renderAutocomplete?.({
                                            searchKey: 'flights.0.startLocation',
                                            label: FLIGHT_PLACEHOLDERS.ORIGIN,
                                            typeAheadEndpoint: 'flights',
                                            errorMessage: errors.flights?.[0]?.startLocation?.message,
                                            initialSelectedItem: flights[0]?.startLocation,
                                            onItemSelect: (item)=>onItemSelect('flights.0.startLocation', item ?? void 0),
                                            inputRef: makeInputRef('flights.0.startLocation')
                                        }),
                                        canShowRouteSwapButton && /*#__PURE__*/ jsx("button", {
                                            type: "button",
                                            "aria-label": "Swap origin and destination",
                                            className: getRouteSwapButtonClass(singleRow),
                                            onClick: handleSwapRoute,
                                            children: /*#__PURE__*/ jsx(CdnIcon, {
                                                iconName: "swap_horiz",
                                                size: "24",
                                                "aria-hidden": true
                                            })
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ jsx("div", {
                                    className: getInputClass('destination', void 0, singleRow),
                                    children: renderAutocomplete?.({
                                        searchKey: 'flights.0.endLocation',
                                        label: FLIGHT_PLACEHOLDERS.DESTINATION,
                                        typeAheadEndpoint: 'flights',
                                        errorMessage: errors.flights?.[0]?.endLocation?.message,
                                        initialSelectedItem: flights[0]?.endLocation,
                                        onItemSelect: (item)=>onItemSelect('flights.0.endLocation', item ?? void 0),
                                        inputRef: makeInputRef('flights.0.endLocation')
                                    })
                                }),
                                /*#__PURE__*/ jsx("div", {
                                    className: getInputClass('timeline', watchFlightType || FLIGHT_DEFAULT_VALUES.FLIGHT_TYPE, singleRow),
                                    children: renderDatePicker ? renderDatePicker({
                                        flightType: watchFlightType,
                                        cabinClass: watchCabinClass || FLIGHT_DEFAULT_VALUES.CABIN_CLASS,
                                        selected: datePickerSelected,
                                        errorMessage: datePickerErrorMessage,
                                        handleCalendarChange: handleFlightCalendarChange,
                                        disabled: {
                                            before: calendarStartMonth,
                                            after: maxSelectableDate
                                        },
                                        startMonth: calendarStartMonth,
                                        endMonth: calendarEndMonth,
                                        defaultMonth,
                                        min: datePickerMin,
                                        isMobile: isMobileComponent,
                                        title: datePickerTitle,
                                        label: datePickerLabel,
                                        mode: datePickerMode,
                                        numberOfMonths: datePickerNumberOfMonths,
                                        inputRef: autoAdvance ? registerTabElement : void 0
                                    }) : /*#__PURE__*/ jsx(DatePicker, {
                                        className: "flight-dates",
                                        title: datePickerTitle,
                                        label: datePickerLabel,
                                        mode: datePickerMode,
                                        numberOfMonths: datePickerNumberOfMonths,
                                        selected: datePickerSelected,
                                        errorMessage: datePickerErrorMessage,
                                        handleCalendarChange: handleFlightCalendarChange,
                                        disabled: {
                                            before: calendarStartMonth,
                                            after: maxSelectableDate
                                        },
                                        startMonth: calendarStartMonth,
                                        endMonth: calendarEndMonth,
                                        defaultMonth: defaultMonth,
                                        min: datePickerMin,
                                        inputRef: autoAdvance ? registerTabElement : void 0,
                                        collisionAvoidance: collisionAvoidance
                                    })
                                })
                            ]
                        }),
                        /*#__PURE__*/ jsx("div", {
                            className: getInputClass('passengers', watchFlightType || FLIGHT_DEFAULT_VALUES.FLIGHT_TYPE, singleRow),
                            children: /*#__PURE__*/ jsx(Controller, {
                                control: control,
                                name: "travelers",
                                defaultValue: watchTravelers,
                                render: ({ field })=>{
                                    const { ref: _ref, ...rest } = field;
                                    return /*#__PURE__*/ jsx(TravelerSelection, {
                                        ...rest,
                                        isHotel: addHotel,
                                        roomsEnabled: addHotel,
                                        tooltipErrorMessageId: "travelerSelectionError",
                                        errorMessage: errors?.travelers?.lapInfants?.message || '',
                                        initialValues: field.value,
                                        shouldCountLapInfant: true,
                                        maxChildren: 6,
                                        isTravelerSelectionDisabled: isTravelerSelectionDisabled,
                                        onChange: (values)=>{
                                            field.onChange({
                                                adults: values.adults,
                                                children: values.children,
                                                lapInfants: values.lapInfants,
                                                childrenAges: values.childrenAges,
                                                rooms: values.rooms
                                            });
                                        },
                                        collisionAvoidance: collisionAvoidance
                                    });
                                }
                            })
                        }),
                        !hideCabin && /*#__PURE__*/ jsx(Select, {
                            label: FLIGHT_LABELS.CABIN_CLASS,
                            isInset: true,
                            iconLeft: "airline_seat_recline_normal",
                            menuItems: [
                                ...CABIN_OPTIONS
                            ],
                            initialValue: watchCabinClass,
                            placeholder: FLIGHT_PLACEHOLDERS.CABIN_CLASS,
                            className: getInputClass('cabin', watchFlightType, singleRow),
                            onChange: (value)=>{
                                setValue('cabinClass', value, {
                                    shouldDirty: true
                                });
                            }
                        }),
                        !isWideLayout && renderBundleAndSaveSection(),
                        /*#__PURE__*/ jsx(SubmitButton, {
                            type: submitButtonType,
                            emphasis: submitButtonEmphasis,
                            className: cn('[grid-area:unset]', '@7xl:[grid-area:unset]', !singleRow && (watchFlightType === MULTI_DESTINATION ? '@3xl:[grid-area:unset]' : '@3xl:[grid-area:submit]'), !singleRow && watchFlightType === ONE_WAY && '@3xl:col-span-full', isMobile ? 'fixed right-4 bottom-4 left-4' : ''),
                            children: submitLabel ?? FLIGHT_PLACEHOLDERS.SEARCH_BUTTON
                        }),
                        isWideLayout && renderBundleAndSaveSection()
                    ]
                })
            })
        })
    });
}
export { FlightSearchForm };
